import { BACK_BUTTON_PRIORITY, useCloseOnBackButton } from "@/hooks/ui/use-close-on-back-button";
import { cn } from "@/utils/cn";
import { memo, useCallback, useState } from "react";
import { createPortal } from "react-dom";

interface SettingsNavButtonProps {
  label: string;
  children: (onClose: () => void) => React.ReactNode;
  className?: string;
  labelClassName?: string;
}

export const SettingsNavButton = memo(function SettingsNavButton({
  label,
  children,
  className,
  labelClassName,
}: SettingsNavButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  // Android back closes this full-screen layer instead of popping the IonNav
  // or navigating the router; Ionic sheets opened inside it still win.
  useCloseOnBackButton(isOpen, handleClose, { priority: BACK_BUTTON_PRIORITY.fullScreenLayer });

  const handleAnimationEnd = useCallback((e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === "modal-slide-down") {
      setIsOpen(false);
      setIsClosing(false);
    }
  }, []);

  return (
    <>
      <button
        type="button"
        className={cn(
          "flex h-[52px] w-full items-center justify-center overflow-hidden",
          "rounded-[27px] border border-[#ebe0fb] bg-white px-[18px]",
          "shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)]",
          "active:bg-[#ebe0fb]",
          className,
        )}
        onClick={() => setIsOpen(true)}
      >
        <span
          className={cn(
            "text-[14px] leading-[14px] font-medium text-ellipsis whitespace-nowrap text-[#620ad5]",
            labelClassName,
          )}
        >
          {label}
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            className={cn(
              "fixed inset-0 z-[10000] bg-white",
              isClosing ? "animate-modal-slide-down" : "animate-modal-slide-up",
            )}
            onAnimationEnd={handleAnimationEnd}
          >
            {children(handleClose)}
          </div>,
          document.querySelector("ion-app") ?? document.body,
        )}
    </>
  );
});
